import { FC, useEffect, useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  BankDetailsRequestPayload,
  useCreateBankDetailsMutation,
  useLazyGetBankDetailsQuery,
  useUpdateBankDetailsMutation,
} from '@api/banks';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import DealSetupFooter from './components/DealSetupFooter';

// Define form field configuration
const BANK_FORM_FIELDS = [
  {
    id: 'bank-name',
    label: 'Full Bank Name',
    fieldName: 'bankName',
    required: true,
  },
  {
    id: 'bank-address',
    label: 'Bank Address',
    fieldName: 'bankAddress',
    required: true,
  },
  {
    id: 'swift-code',
    label: 'SWIFT Code',
    fieldName: 'swiftCode',
    required: true,
  },
  {
    id: 'iban',
    label: 'IBAN',
    fieldName: 'ibanCode',
    required: true,
  },
  {
    id: 'account-number',
    label: 'Account Number',
    fieldName: 'accountNumber',
    required: true,
  },
  {
    id: 'routing-number',
    label: 'Routing Number (if needed)',
    fieldName: 'routingNumber',
    required: false,
  },
];

// Define form data interface
interface BankFormData {
  bankName: string;
  bankAddress: string;
  swiftCode: string;
  ibanCode: string;
  accountNumber: string;
  routingNumber: string;
}

// Define touched and error state interfaces
interface FormTouchedState {
  bankName: boolean;
  bankAddress: boolean;
  swiftCode: boolean;
  ibanCode: boolean;
  accountNumber: boolean;
  routingNumber: boolean;
}

interface FormErrorState {
  bankName: boolean;
  bankAddress: boolean;
  swiftCode: boolean;
  ibanCode: boolean;
  accountNumber: boolean;
  routingNumber: boolean;
}

interface BankDetailsProps {
  gameId: string;
  handleCancelClick: () => void;
  dealUpdatedDetailsComponent: JSX.Element;
}

const BankDetails: FC<BankDetailsProps> = ({
  gameId,
  handleCancelClick,
  dealUpdatedDetailsComponent,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState<BankFormData>({
    bankName: '',
    bankAddress: '',
    swiftCode: '',
    ibanCode: '',
    accountNumber: '',
    routingNumber: '',
  });

  // Initialize touched state with all fields false
  const [touched, setTouched] = useState<FormTouchedState>({
    bankName: false,
    bankAddress: false,
    swiftCode: false,
    ibanCode: false,
    accountNumber: false,
    routingNumber: false,
  });

  // Initialize error state with empty strings
  const [errors, setErrors] = useState<FormErrorState>({
    bankName: true,
    bankAddress: true,
    swiftCode: true,
    ibanCode: true,
    accountNumber: true,
    routingNumber: false,
  });

  const [bankId, setBankId] = useState<string | null>(null);

  // API hooks
  const [createBankDetails] = useCreateBankDetailsMutation();
  const [updateBankDetails] = useUpdateBankDetailsMutation();
  const [fetchBankDetails] = useLazyGetBankDetailsQuery();

  // Fetch bank details on component mount if gameId exists
  useEffect(() => {
    loadBankDetails();
  }, [gameId]);

  const loadBankDetails = async () => {
    if (!gameId) return;

    try {
      const response = await fetchBankDetails(gameId);
      const { data } = response.data;

      if (data) {
        setBankId(data.id);
        setFormData({
          bankName: data.bankName,
          bankAddress: data.bankAddress,
          swiftCode: data.swiftCode,
          ibanCode: data.ibanCode,
          accountNumber: data.accountNumber,
          routingNumber: data.routingNumber,
        });

        // Validate the loaded data
        validateForm({
          bankName: data.bankName,
          bankAddress: data.bankAddress,
          swiftCode: data.swiftCode,
          ibanCode: data.ibanCode,
          accountNumber: data.accountNumber,
          routingNumber: data.routingNumber,
        });
      }
    } catch (error) {
      // Silent failure - could add error handling here if needed
    }
  };

  // Check if form is valid
  const isFormValid = useMemo(() => {
    return Object.values(errors).every((error) => error === false);
  }, [errors]);

  // Validate a single field
  const validateField = (fieldName: string, value: string): boolean => {
    const field = BANK_FORM_FIELDS.find((f) => f.fieldName === fieldName);

    if (!field) return false;

    // Check required fields
    if (field.required && !value.trim()) {
      return true;
    }

    return false;
  };

  // Validate all form fields
  const validateForm = (data: BankFormData) => {
    const newErrors = {} as FormErrorState;

    Object.keys(data).forEach((key) => {
      const fieldName = key as keyof BankFormData;
      newErrors[fieldName] = validateField(key, data[fieldName]);
    });

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === false);
  };

  // Handle input field changes
  const handleFieldChange = (fieldName: string, value: string) => {
    // Update form data
    setFormData((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));

    // Mark field as touched
    setTouched((prevTouched) => ({
      ...prevTouched,
      [fieldName]: true,
    }));

    // Validate field and update error
    const errorMessage = validateField(fieldName, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: errorMessage,
    }));
  };

  // Handle save button click
  const handleSave = async () => {
    // Set all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key as keyof FormTouchedState] = true;
      return acc;
    }, {} as FormTouchedState);

    setTouched(allTouched);

    // Validate all fields before saving
    const isValid = validateForm(formData);

    if (!isValid) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Please fix the errors before saving.',
        }),
      );
      return;
    }

    try {
      setLoader(true);

      const payload: BankDetailsRequestPayload = {
        gameId,
        ...formData,
      };

      if (bankId) {
        await updateBankDetails({ bankId, ...payload }).unwrap();
      } else {
        await createBankDetails(payload).unwrap();
      }

      await loadBankDetails();
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || 'Updating Bank Details failed. Please try again.',
        }),
      );
    } finally {
      setLoader(false);
    }
  };

  // Handle field blur for touch state
  const handleFieldBlur = (fieldName: string) => {
    // Mark field as touched
    setTouched((prevTouched) => ({
      ...prevTouched,
      [fieldName]: true,
    }));

    // Validate on blur
    const errorMessage = validateField(fieldName, formData[fieldName as keyof BankFormData]);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: errorMessage,
    }));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Form content */}
      <Box
        id="bank-details-form"
        sx={{
          flex: '1',
          pr: '0.5rem',
          overflowY: 'auto',
          pb: '1rem',
        }}
      >
        {loader ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              style={{
                width: '4rem',
                height: '4rem',
                color: theme.palette.primary.main,
              }}
            />
          </Box>
        ) : (
          <>
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              sx={{ color: '#162936', marginBottom: '1.25rem' }}
            >
              Bank Details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1rem',
                marginBottom: '2rem',
              }}
            >
              {BANK_FORM_FIELDS.map((field) => (
                <TextField
                  key={field.id}
                  id={field.id}
                  label={field.label}
                  value={formData[field.fieldName as keyof BankFormData]}
                  error={
                    touched[field.fieldName as keyof FormTouchedState] &&
                    !!errors[field.fieldName as keyof FormErrorState]
                  }
                  required={field.required}
                  onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                  onBlur={() => handleFieldBlur(field.fieldName as keyof BankFormData)}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Footer with action buttons */}
      <DealSetupFooter
        dealUpdatedDetailsComponent={dealUpdatedDetailsComponent}
        onCancel={handleCancelClick}
        isSubmitEnabled={isFormValid}
        onSave={handleSave}
      />
    </Box>
  );
};

export default BankDetails;
